import { state } from '../state';
import { loop, hasHaptics } from '../loopSdk';
import type { Item } from '../types';
import { isReducedMotion } from '../utils/reducedMotion';
import { isInstallerFlowActive } from '../layout/ringLayout';
import { LINE_CENTER_RADIUS } from '../layout/ringLayout';

// ── Matrix Text Scramble ──────────────────────────────────────────────────
// Vestigial state retained for future scramble animation; assigned but not yet read
// @ts-ignore -- noUnusedLocals: reserved
const SCRAMBLE_CHARS = 'abcdefghijklmnopqrstuvwxyz0123456789@#$%&*';
let scrambleRafId: number | null = null;
// @ts-ignore -- noUnusedLocals: reserved
let scrambleTarget = '•';
const reticleInner = document.querySelector('#reticle .inner') as HTMLElement;
export const RETICLE_LOCK_TEXT = '🔒\uFE0E';
// RETICLE_LOCK_SVG uses innerHTML (existing pattern, trusted static string)
const RETICLE_LOCK_SVG = '<svg class="lock-cue-icon" width="6" height="8" viewBox="0 0 6 8" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false"><path fill-rule="evenodd" clip-rule="evenodd" d="M0.6 3.36H0.73V2.12C0.76 1.53 1.01 1.01 1.41 0.63C1.81 0.24 2.36 0 2.95 0C3.54 0 4.09 0.24 4.5 0.63C4.9 1.02 5.16 1.56 5.18 2.16V3.37H5.31C5.47 3.37 5.63 3.44 5.73 3.55L5.76 3.58C5.85 3.69 5.91 3.82 5.91 3.97V7.03C5.91 7.19 5.84 7.35 5.73 7.45C5.62 7.56 5.47 7.63 5.31 7.63H0.6C0.44 7.63 0.28 7.56 0.18 7.45C0.07 7.34 0 7.19 0 7.03V3.97C0 3.81 0.07 3.66 0.18 3.55C0.29 3.44 0.44 3.37 0.6 3.37V3.36ZM1.45 3.36H4.46V2.16C4.45 1.76 4.27 1.4 4 1.14C3.73 0.88 3.36 0.72 2.95 0.72C2.54 0.72 2.17 0.88 1.9 1.14C1.63 1.4 1.46 1.75 1.44 2.15V3.36H1.45Z" fill="white"/></svg>';
let blockedActionResetTimer: ReturnType<typeof setTimeout> | null = null;
let reticleDisplayedText = '';
let reticleFlipVersion = 0;
let reticleFlipCleanupTimer: ReturnType<typeof setTimeout> | null = null;
let reticleFlipStack: HTMLElement | null = null;
let reticleCurrentSlot: HTMLElement | null = null;
let reticleNextSlot: HTMLElement | null = null;
const RETICLE_FLIP_MS = 460;
const RETICLE_LINE_MASK_PAD = 0;
const RETICLE_MIN_INNER_WIDTH = 12;
const RETICLE_WIDTH_LERP = 0.22;
const RETICLE_WIDTH_EPSILON = 0.2;
let reticleInnerWidthCurrent = RETICLE_MIN_INNER_WIDTH;
let reticleInnerWidthTarget = RETICLE_MIN_INNER_WIDTH;
let reticleWidthLerpRafId: number | null = null;
const reticleBrackets = Array.from(document.querySelectorAll('#reticle .bracket')) as HTMLElement[];

// Callbacks for cross-module dependencies still in index.html
let resetToLock: () => void = () => {};
let selectItem: (item: Item) => void = (_item) => {};
export function setResetToLockRef(fn: () => void): void { resetToLock = fn; }
export function setSelectItemForScramble(fn: (item: Item) => void): void { selectItem = fn; }

function getReticleBracketMaskHeight() {
  let maxBracketHeight = 0;
  for (const bracket of reticleBrackets) {
    const h = bracket.getBoundingClientRect().height || 0;
    if (h > maxBracketHeight) maxBracketHeight = h;
  }
  return maxBracketHeight;
}

function setReticleLineHeight(pxHeight: number) {
  const bracketHeight = getReticleBracketMaskHeight();
  const basisHeight = bracketHeight > 0 ? bracketHeight : (pxHeight || 0);
  const safeHeight = Math.max(1, Math.ceil(basisHeight + RETICLE_LINE_MASK_PAD));
  reticleInner.style.setProperty('--reticle-line-height', `${safeHeight}px`);
}

function normalizeReticleWidth(pxWidth: number) {
  return Math.max(RETICLE_MIN_INNER_WIDTH, Math.ceil(pxWidth || 0));
}

function setReticleInnerWidth(pxWidth: number) {
  reticleInner.style.width = `${normalizeReticleWidth(pxWidth)}px`;
}

function stepReticleWidthLerp() {
  reticleInnerWidthCurrent = reticleInnerWidthCurrent + (reticleInnerWidthTarget - reticleInnerWidthCurrent) * RETICLE_WIDTH_LERP;
  if (Math.abs(reticleInnerWidthTarget - reticleInnerWidthCurrent) <= RETICLE_WIDTH_EPSILON) {
    reticleInnerWidthCurrent = reticleInnerWidthTarget;
    setReticleInnerWidth(reticleInnerWidthCurrent);
    reticleWidthLerpRafId = null;
    return;
  }
  setReticleInnerWidth(reticleInnerWidthCurrent);
  reticleWidthLerpRafId = requestAnimationFrame(stepReticleWidthLerp);
}

function setReticleWidthImmediate(pxWidth: number) {
  if (reticleWidthLerpRafId) cancelAnimationFrame(reticleWidthLerpRafId);
  reticleWidthLerpRafId = null;
  const normalized = normalizeReticleWidth(pxWidth);
  reticleInnerWidthCurrent = normalized;
  reticleInnerWidthTarget = normalized;
  setReticleInnerWidth(normalized);
}

function lerpReticleWidthTo(pxWidth: number) {
  const normalized = normalizeReticleWidth(pxWidth);
  if (isReducedMotion()) {
    setReticleWidthImmediate(normalized);
    return;
  }
  reticleInnerWidthTarget = normalized;
  if (!reticleWidthLerpRafId) {
    reticleWidthLerpRafId = requestAnimationFrame(stepReticleWidthLerp);
  }
}

function writeReticleSlot(slot: HTMLElement, text: string, isLockCue: boolean) {
  slot.dataset.value = text;
  slot.dataset.lockCue = isLockCue ? '1' : '0';
  if (isLockCue) slot.innerHTML = RETICLE_LOCK_SVG;
  else slot.textContent = text;
}

function resetReticleSlotShadowClasses() {
  if (reticleCurrentSlot) reticleCurrentSlot.classList.remove('shadow-in', 'shadow-out');
  if (reticleNextSlot) reticleNextSlot.classList.remove('shadow-in', 'shadow-out');
}

function createReticleSlot(text: string, isLockCue: boolean) {
  const slot = document.createElement('span');
  slot.className = 'reticle-text-slot';
  writeReticleSlot(slot, text, isLockCue);
  return slot;
}

function ensureReticleFlipStack(text: string, isLockCue: boolean) {
  const stack = document.createElement('span');
  stack.className = 'reticle-flip-stack';
  const currentSlot = createReticleSlot(text, isLockCue);
  const nextSlot = createReticleSlot(text, isLockCue);
  stack.appendChild(currentSlot);
  stack.appendChild(nextSlot);
  reticleInner.replaceChildren(stack);
  reticleFlipStack = stack;
  reticleCurrentSlot = currentSlot;
  reticleNextSlot = nextSlot;
  reticleInner.style.setProperty('--reticle-flip-duration', `${RETICLE_FLIP_MS}ms`);
  const currentHeight = currentSlot.getBoundingClientRect().height || 0;
  const nextHeight = nextSlot.getBoundingClientRect().height || 0;
  const currentWidth = currentSlot.getBoundingClientRect().width || 0;
  const nextWidth = nextSlot.getBoundingClientRect().width || 0;
  setReticleLineHeight(Math.max(currentHeight, nextHeight));
  setReticleWidthImmediate(Math.max(currentWidth, nextWidth));
  reticleDisplayedText = text;
}

function finalizeReticleFlip() {
  if (!reticleFlipStack || !reticleCurrentSlot || !reticleNextSlot) return;
  if (!reticleFlipStack.classList.contains('flipping')) return;
  if (reticleFlipCleanupTimer) {
    clearTimeout(reticleFlipCleanupTimer);
    reticleFlipCleanupTimer = null;
  }
  const nextText = reticleNextSlot.dataset.value || reticleDisplayedText || '•';
  const nextIsLockCue = reticleNextSlot.dataset.lockCue === '1';
  reticleFlipStack.classList.remove('flipping');
  resetReticleSlotShadowClasses();
  writeReticleSlot(reticleCurrentSlot, nextText, nextIsLockCue);
  writeReticleSlot(reticleNextSlot, nextText, nextIsLockCue);
  reticleDisplayedText = nextText;
  const lineHeight = reticleCurrentSlot.getBoundingClientRect().height || 0;
  const lineWidth = reticleCurrentSlot.getBoundingClientRect().width || 0;
  setReticleLineHeight(lineHeight);
  lerpReticleWidthTo(lineWidth);
}

function setReticleTextImmediate(text: string, isLockCue: boolean) {
  if (reticleFlipCleanupTimer) {
    clearTimeout(reticleFlipCleanupTimer);
    reticleFlipCleanupTimer = null;
  }
  if (!reticleFlipStack || !reticleCurrentSlot || !reticleNextSlot) {
    ensureReticleFlipStack(text, isLockCue);
    return;
  }
  reticleFlipStack.classList.remove('flipping');
  resetReticleSlotShadowClasses();
  writeReticleSlot(reticleCurrentSlot, text, isLockCue);
  writeReticleSlot(reticleNextSlot, text, isLockCue);
  const lineHeight = reticleCurrentSlot.getBoundingClientRect().height || 0;
  const lineWidth = reticleCurrentSlot.getBoundingClientRect().width || 0;
  setReticleLineHeight(lineHeight);
  setReticleWidthImmediate(lineWidth);
  reticleDisplayedText = text;
}

function initializeReticleFlip() {
  const initialText = (reticleInner.textContent || '•').trim() || '•';
  ensureReticleFlipStack(initialText, initialText === RETICLE_LOCK_TEXT);
  reticleInner.classList.toggle('lock-cue', initialText === RETICLE_LOCK_TEXT);
}

export function scrambleToText(text: string) {
  scrambleTarget = text;
  if (scrambleRafId) cancelAnimationFrame(scrambleRafId);
  scrambleRafId = null;
  const isLockCue = text === RETICLE_LOCK_TEXT;
  reticleInner.classList.toggle('lock-cue', isLockCue);
  if (!reticleFlipStack || !reticleCurrentSlot || !reticleNextSlot) {
    ensureReticleFlipStack(reticleDisplayedText || text, reticleDisplayedText === RETICLE_LOCK_TEXT);
  }
  finalizeReticleFlip();
  if (text === reticleDisplayedText) return;
  if (isReducedMotion()) {
    setReticleTextImmediate(text, isLockCue);
    return;
  }

  const flipVersion = ++reticleFlipVersion;
  writeReticleSlot(reticleNextSlot!, text, isLockCue);
  const currentHeight = reticleCurrentSlot!.getBoundingClientRect().height || 0;
  const nextHeight = reticleNextSlot!.getBoundingClientRect().height || 0;
  const currentWidth = reticleCurrentSlot!.getBoundingClientRect().width || 0;
  const nextWidth = reticleNextSlot!.getBoundingClientRect().width || 0;
  setReticleLineHeight(Math.max(currentHeight, nextHeight));
  lerpReticleWidthTo(Math.max(currentWidth, nextWidth));
  reticleFlipStack!.classList.remove('flipping');
  resetReticleSlotShadowClasses();
  void reticleFlipStack!.offsetHeight;

  requestAnimationFrame(() => {
    if (flipVersion !== reticleFlipVersion) return;
    reticleCurrentSlot!.classList.add('shadow-out');
    reticleNextSlot!.classList.add('shadow-in');
    reticleFlipStack!.classList.add('flipping');
  });

  if (reticleFlipCleanupTimer) clearTimeout(reticleFlipCleanupTimer);
  reticleFlipCleanupTimer = setTimeout(() => {
    if (flipVersion !== reticleFlipVersion) return;
    reticleFlipStack!.classList.remove('flipping');
    resetReticleSlotShadowClasses();
    writeReticleSlot(reticleCurrentSlot!, text, isLockCue);
    writeReticleSlot(reticleNextSlot!, text, isLockCue);
    const lineHeight = reticleCurrentSlot!.getBoundingClientRect().height || nextHeight;
    const lineWidth = reticleCurrentSlot!.getBoundingClientRect().width || nextWidth;
    setReticleLineHeight(lineHeight);
    lerpReticleWidthTo(lineWidth);
    reticleDisplayedText = text;
    reticleFlipCleanupTimer = null;
  }, RETICLE_FLIP_MS + 24);
}

initializeReticleFlip();

export function setReticleEmptyCarrier(diameterPx: number) {
  // Empty content, custom width — used by the edit-mode drag so the
  // brackets visually "carry" the dragged icon (the icon nestles between
  // them at any scale). Cheap to call per-frame: setReticleTextImmediate
  // is a no-op when the displayed text already matches, and lerpReticle-
  // WidthTo just updates the lerp target.
  if (reticleDisplayedText !== ' ') {
    setReticleTextImmediate(' ', false);
  }
  lerpReticleWidthTo(diameterPx);
}

export function signalBlockedInstallerAction() {
  scrambleToText(' busy ');
  if (hasHaptics) loop!.haptics!.pulse(0.12);
  if (blockedActionResetTimer) clearTimeout(blockedActionResetTimer);
  const resetDelay = isReducedMotion() ? 260 : 420;
  blockedActionResetTimer = setTimeout(() => {
    if (state.appState !== 'home') return;
    if (state.highlighted) scrambleToText(' ' + state.highlighted.name + ' ');
    else scrambleToText(getHomeIdleReticleText());
  }, resetDelay);
}

export function getHomeIdleReticleText() {
  return isHomeCenterZoneActive() ? RETICLE_LOCK_TEXT : '•';
}

export function isHomeCenterZoneActive() {
  if (state.appState !== 'home') return false;
  return Math.hypot(state.cameraX, state.cameraY) <= LINE_CENTER_RADIUS;
}

export function handleHomePrimaryAction() {
  if (state.appState !== 'home') return;
  const mode = state.homeUI.mode;
  if (mode !== 'normal' && mode !== 'options_exiting') return;
  if (isHomeCenterZoneActive()) {
    if (mode !== 'normal') return;
    resetToLock();
    return;
  }
  if (isInstallerFlowActive()) {
    signalBlockedInstallerAction();
    return;
  }
  if (state.highlighted) selectItem(state.highlighted);
}
